/**
 * @file Toast.tsx
 * @description Global toast notification system.
 * Supports: success, error, warning, info.
 * Special toasts:
 *  - 409 conflict  → "Data modified by another user. Refresh to see latest." + [Refresh Now] button
 *  - circuit-breaker → "Service temporarily unavailable. Please try again shortly."
 *  - ptm-slot       → "This slot was just booked by another parent. Please select a different time."
 *  - reward-conflict → "Points balance changed. Please refresh."
 */
import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';

export type ToastType = 'success' | 'error' | 'warning' | 'info';
export type ToastSpecial = '409' | 'circuit-breaker' | 'ptm-slot' | 'reward-conflict';

interface ToastItem {
  id: string;
  type: ToastType;
  message: string;
  /** If set, renders an action button */
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface ToastContextValue {
  /** Show a standard toast */
  toast: (message: string, type?: ToastType) => void;
  /** Show a special pre-defined toast */
  toastSpecial: (variant: ToastSpecial) => void;
  /** Show a success toast */
  success: (message: string) => void;
  /** Show an error toast */
  error: (message: string) => void;
  /** Show a warning toast */
  warning: (message: string) => void;
  /** Show an info toast */
  info: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const TOAST_COLORS: Record<ToastType, { bg: string; border: string; color: string; icon: string }> = {
  success: { bg: 'var(--green-lt)', border: 'var(--green)',  color: 'var(--green)',  icon: '✅' },
  error:   { bg: 'var(--red-lt)',   border: 'var(--red)',    color: 'var(--red)',    icon: '❌' },
  warning: { bg: 'var(--amber-lt)', border: 'var(--amber)',  color: 'var(--amber)',  icon: '⚠️' },
  info:    { bg: 'var(--sky-lt)',   border: 'var(--sky)',    color: 'var(--sky)',    icon: 'ℹ️' },
};

let counter = 0;
function genId() { return `toast-${++counter}`; }

/**
 * ToastProvider
 * Wrap the app with this provider to enable the useToast hook.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const queryClient = useQueryClient();
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const t = timersRef.current.get(id);
    if (t) { clearTimeout(t); timersRef.current.delete(id); }
  }, []);

  const addToast = useCallback((item: Omit<ToastItem, 'id'>, duration = 5000) => {
    const id = genId();
    setToasts((prev) => [...prev.slice(-3), { ...item, id }]); // max 4 toasts
    const timer = setTimeout(() => dismiss(id), duration);
    timersRef.current.set(id, timer);
  }, [dismiss]);

  const toast = useCallback((message: string, type: ToastType = 'info') => {
    addToast({ type, message });
  }, [addToast]);

  const success = useCallback((message: string) => addToast({ type: 'success', message }), [addToast]);
  const error   = useCallback((message: string) => addToast({ type: 'error',   message }), [addToast]);
  const warning = useCallback((message: string) => addToast({ type: 'warning', message }), [addToast]);
  const info    = useCallback((message: string) => addToast({ type: 'info',    message }), [addToast]);

  const toastSpecial = useCallback((variant: ToastSpecial) => {
    switch (variant) {
      case '409':
        addToast({
          type: 'warning',
          message: 'Data modified by another user. Refresh to see latest.',
          action: {
            label: 'Refresh Now',
            onClick: () => queryClient.invalidateQueries(),
          },
        }, 8000);
        break;
      case 'circuit-breaker':
        addToast({
          type: 'warning',
          message: 'Service temporarily unavailable. Please try again shortly.',
        }, 6000);
        break;
      case 'ptm-slot':
        addToast({
          type: 'error',
          message: 'This slot was just booked by another parent. Please select a different time.',
        });
        break;
      case 'reward-conflict':
        addToast({
          type: 'warning',
          message: 'Points balance changed. Please refresh.',
          action: {
            label: 'Refresh',
            onClick: () => queryClient.invalidateQueries(),
          },
        });
        break;
    }
  }, [addToast, queryClient]);

  return (
    <ToastContext.Provider value={{ toast, toastSpecial, success, error, warning, info }}>
      {children}

      {/* Toast container — bottom-center on mobile, bottom-right on desktop */}
      <div
        aria-live="polite"
        aria-atomic="false"
        style={{
          position: 'fixed',
          bottom: 'calc(var(--bottom-nav-h, 68px) + 12px)',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 'var(--z-toast)' as unknown as number,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          alignItems: 'center',
          pointerEvents: 'none',
          width: 'min(calc(100vw - 32px), 420px)',
        }}
      >
        {toasts.map((t) => {
          const cfg = TOAST_COLORS[t.type];
          return (
            <div
              key={t.id}
              role="alert"
              style={{
                background: cfg.bg,
                border: `1.5px solid ${cfg.border}`,
                borderRadius: 14,
                padding: '10px 14px',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                width: '100%',
                boxShadow: 'var(--shadow-lg)',
                animation: 'toastIn 0.25s ease',
                pointerEvents: 'all',
              }}
            >
              <span style={{ fontSize: 16, flexShrink: 0 }}>{cfg.icon}</span>
              <span
                style={{
                  flex: 1,
                  fontSize: 13,
                  fontWeight: 600,
                  color: cfg.color,
                  lineHeight: 1.4,
                }}
              >
                {t.message}
              </span>

              {/* Action button (e.g. "Refresh Now") */}
              {t.action && (
                <button
                  onClick={() => { t.action!.onClick(); dismiss(t.id); }}
                  style={{
                    background: cfg.border,
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    padding: '4px 10px',
                    fontSize: 11,
                    fontWeight: 700,
                    cursor: 'pointer',
                    flexShrink: 0,
                    fontFamily: 'var(--font-body)',
                    minHeight: 28,
                  }}
                >
                  {t.action.label}
                </button>
              )}

              {/* Dismiss button */}
              <button
                onClick={() => dismiss(t.id)}
                aria-label="Dismiss notification"
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: cfg.color,
                  fontSize: 16,
                  lineHeight: 1,
                  padding: 2,
                  flexShrink: 0,
                  minWidth: 24,
                  minHeight: 24,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                ×
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

/**
 * useToast
 * Returns toast helper functions. Must be used inside ToastProvider.
 * @returns ToastContextValue
 */
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}